import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useFetch } from "@/lib/shieldx";
import { PageHeader, Loader, Panel } from "@/components/shieldx/Primitives";
import { SAStat } from "@/components/shieldx/SuperAdmin";
import {
  ShieldCheck, Building2, Boxes, Siren, Crosshair, UsersRound, Plug, Bell,
  OctagonAlert, FileLock2, Satellite, ShieldHalf, ShieldUser, Landmark,
} from "lucide-react";

export default function SAOverview() {
  const { data, loading } = useFetch("/super-admin/overview");
  const navigate = useNavigate();
  if (loading || !data) return <Loader label="Loading global governance overview" />;

  const o = data.organizations, a = data.assets, inc = data.incidents;
  const cat = [
    { label: "Enterprise", value: o.enterprise, to: "/app/super-admin/organizations", icon: Building2 },
    { label: "Government", value: o.government, to: "/app/super-admin/government", icon: Landmark },
    { label: "Defense", value: o.defense, to: "/app/super-admin/defense", icon: ShieldHalf },
    { label: "Aerospace", value: o.aerospace, to: "/app/super-admin/aerospace", icon: Satellite },
    { label: "Creators", value: o.creators, to: "/app/super-admin/creators", icon: ShieldUser },
    { label: "Other", value: o.other, to: "/app/super-admin/organizations", icon: Building2 },
  ];

  return (
    <div>
      <PageHeader icon={ShieldCheck} title="Super Admin Command Center"
        subtitle="Authoritative governance across the entire ShieldX ecosystem. Every value is live platform state. Every privileged action is audited." />

      <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Organizations</div>
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <SAStat label="Total" value={o.total} icon={Building2} delay={0} onClick={() => navigate("/app/super-admin/organizations")} testid="kpi-orgs-total" />
        <SAStat label="Verified" value={o.verified} tone="emerald" delay={0.03} />
        <SAStat label="Pending" value={o.pending} tone="amber" delay={0.06} />
        <SAStat label="Suspended" value={o.suspended} tone="red" delay={0.09} />
        <SAStat label="Revoked" value={o.revoked} tone="red" delay={0.12} />
        <SAStat label="Creators" value={o.creators} tone="cyan" delay={0.15} onClick={() => navigate("/app/super-admin/creators")} />
      </div>

      <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Assets & Protection</div>
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <SAStat label="Protected Assets" value={a.protected} tone="blue" icon={Boxes} onClick={() => navigate("/app/super-admin/assets")} testid="kpi-assets-protected" />
        <SAStat label="Total Assets" value={a.total} />
        <SAStat label="Protection Degraded" value={a.degraded} tone="amber" />
        <SAStat label="Verification Required" value={a.verification_required} tone="cyan" />
      </div>

      <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Incidents, Simulations & Users</div>
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <SAStat label="Active Incidents" value={inc.active} tone="amber" icon={Siren} onClick={() => navigate("/app/incidents")} />
        <SAStat label="Critical" value={inc.critical} tone="red" />
        <SAStat label="Active Simulations" value={data.simulations.active} tone="purple" icon={Crosshair} />
        <SAStat label="Total Users" value={data.users.total} icon={UsersRound} onClick={() => navigate("/app/super-admin/users")} testid="kpi-users" />
        <SAStat label="Active Integrations" value={data.integrations.active} tone="emerald" icon={Plug} />
        <SAStat label="Failed Integrations" value={data.integrations.failed} tone="red" />
      </div>

      <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Security & Audit</div>
      <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-3">
        <SAStat label="Security Alerts" value={data.security_alerts} tone="amber" icon={Bell} />
        <SAStat label="Emergency Actions" value={data.emergency_actions} tone="red" icon={OctagonAlert} onClick={() => navigate("/app/emergency")} />
        <SAStat label="Super Admin Audit Events" value={data.audit_events} tone="cyan" icon={FileLock2} onClick={() => navigate("/app/super-admin/audit")} testid="kpi-audit" />
      </div>

      <Panel title="Governance by Category" desc="Open any category to see its 360° organizations and governance controls.">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          {cat.map((c, i) => (
            <motion.button key={c.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              onClick={() => navigate(c.to)} data-testid={`cat-${c.label.toLowerCase()}`}
              className="group rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-left transition-colors hover:border-blue-500/40 hover:bg-slate-900/80">
              <c.icon className="h-5 w-5 text-slate-500 group-hover:text-blue-400" />
              <p className="mt-2 font-display text-2xl font-bold text-slate-100">{c.value}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{c.label}</p>
            </motion.button>
          ))}
        </div>
      </Panel>
    </div>
  );
}
